import React, { useRef } from 'react';
import { CircularProgress, Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import Button from 'src/presentations/components/atom/button';
import { LegalEntityAddressDto } from 'src/generated/services/TFinancialApi';
import { useLegalEntityDetail } from 'src/use-cases/settings/useLegalEntities';
import { openToast } from 'src/use-cases/toast/useToast';
import { LegalEntityAliasTable } from './tables/LegalEntityAliasTable';
import { LegalEntityAddressTable } from './tables/LegalEntityAddressTable';

interface LegalEntityDetailProps {
  legalEntityId: number | null;
  onClose: () => void;
}

export const LegalEntityDetail: React.FC<LegalEntityDetailProps> = ({ legalEntityId, onClose }) => {
  const { data, isLoading } = useLegalEntityDetail(legalEntityId || undefined);
  const legalEntity = data?.data;
  const addressTableRef = useRef<any>(null);
  const aliasTableRef = useRef<any>(null);

  // Handle edit address
  const handleEditAddress = (address: LegalEntityAddressDto) => {
    openToast({
      toastType: 'info',
      title: 'Edit Address',
      description: `Editing address in ${address.city} (ID: ${address.id})`,
    });
    // Here you would typically open a modal or navigate to an edit page
    // Implementation for editing address would go here
  };

  if (!legalEntityId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
        <Box display='flex' justifyContent='center' alignItems='center' p={4}>
          <CircularProgress color='primary' />
        </Box>
      </div>
    );
  }

  if (!legalEntity) {
    return (
      <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
        <div className='p-4'>
          <p className='text-base'>No legal entity information found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
      {/* Close Button - Positioned at the top right */}
      <div className='absolute -top-2 right-4 z-10'>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      {/* Alias Section */}
      <div className='relative border-b border-neutral-20'>
        <div className='p-24 flex items-center justify-between gap-2'>
          <div className='flex flex-col gap-4'>
            <h3 className='text-20 font-bold'>Alias</h3>
            <p className='text-14 text-neutral-80'>Legal Entity Aliases</p>
          </div>
          <IconButton
            onClick={() => {
              // Get reference to the alias table component and trigger create mode
              if (aliasTableRef.current) {
                aliasTableRef.current.setCreatingRow(true);
              }
            }}
            sx={{
              width: '30px',
              height: '30px',
              border: '1px solid #DFDFDF',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: '#00992B',
                '& .MuiSvgIcon-root': {
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
          </IconButton>
        </div>
        <LegalEntityAliasTable aliases={legalEntity.nameAliases || []} legalEntityId={legalEntity.id} ref={aliasTableRef} />
      </div>

      {/* Address Section */}
      <div className='relative border-b border-neutral-20 mb-[24px]'>
        <div className='p-24 flex items-center justify-between'>
          <div>
            <h3 className='text-20 font-bold mb-2'>Address</h3>
            <p className='text-14 text-neutral-80'>Legal Entity Adresses</p>
          </div>
          <IconButton
            onClick={() => {
              // Get reference to the address table component and trigger create mode
              if (addressTableRef.current) {
                addressTableRef.current.setCreatingRow(true);
              }
            }}
            sx={{
              width: '30px',
              height: '30px',
              border: '1px solid #DFDFDF',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: '#00992B',
                '& .MuiSvgIcon-root': {
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
          </IconButton>
        </div>
        <LegalEntityAddressTable
          addresses={legalEntity.addresses || []}
          legalEntityId={legalEntity.id}
          onEditAddress={handleEditAddress}
          ref={addressTableRef}
        />
      </div>
    </div>
  );
};
